import { useState, useEffect } from 'react';
import { getSchoolId, getSchoolName, setSchoolName } from '../services/auth';
import { fetchSchool, type SchoolApiRecord } from '../services/schoolApi';

export interface CurrentSchool {
    name: string | null;
    /** Street, city, state and postcode joined into the single line a receipt prints. */
    address: string | null;
}

/** "12 MG Road, Bengaluru, Karnataka 560001" — from whichever parts the school has. */
function composeAddress(s: SchoolApiRecord): string | null {
    const line = [s.address, s.city, s.state].map((p) => p?.trim()).filter(Boolean).join(', ');
    const full = [line, s.postal_code?.trim()].filter(Boolean).join(' ');
    return full.length > 0 ? full : null;
}

/**
 * Resolves the currently selected school for printouts/receipts.
 *
 * Seeds the name from the cached value (localStorage) for an instant render, then
 * confirms it against the API — which is also where the address comes from, since only
 * the name is cached. A receipt printed before that lands simply omits the address.
 */
export function useCurrentSchool(): CurrentSchool {
    const [school, setSchool] = useState<CurrentSchool>(() => ({ name: getSchoolName(), address: null }));

    useEffect(() => {
        const id = getSchoolId();
        if (!id) return;
        let cancelled = false;
        fetchSchool(id)
            .then((s) => {
                if (cancelled || !s?.name) return;
                setSchool({ name: s.name, address: composeAddress(s) });
                setSchoolName(s.name);
            })
            .catch(() => { /* keep the cached name on failure */ });
        return () => { cancelled = true; };
    }, []);

    return school;
}

/** Name-only view, for printouts that don't show the address. */
export function useCurrentSchoolName(): string | null {
    return useCurrentSchool().name;
}
